import React, { useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, Button, Paper, Chip, Alert, CircularProgress, Collapse, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';

const MAAND = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const kortePeriode = (p) => `${MAAND[Number(p.slice(5, 7)) - 1]} '${p.slice(2, 4)}`;
const kleurDelta = (d, drempel = 250) => (Math.abs(d) < drempel ? '#64748b' : d > 0 ? '#b45309' : '#b91c1c');
const SIGNAAL = {
    bbi: { label: 'BBI wijkt af', kleur: '#dc2626', uitleg: 'Omzet min inkoop in de tool verschilt van de BBI in het werkboek' },
    planning: { label: 'Planning wijkt af', kleur: '#d97706', uitleg: 'In minstens één maand verschilt het geplande bedrag' },
    excel_dicht: { label: 'Dicht in Excel', kleur: '#6b7280', uitleg: 'Het werkboek noemt het project afgerond of gefactureerd, maar de tool plant nog omzet' },
};

function MaandRegels({ maanden, formatEuro }) {
    return (
        <Table size="small" sx={{ '& td, & th': { py: 0.25, fontSize: 12 } }}>
            <TableHead>
                <TableRow>
                    <TableCell>Maand</TableCell>
                    <TableCell align="right">Tool %</TableCell>
                    <TableCell align="right">Tool €</TableCell>
                    <TableCell align="right">Werkboek %</TableCell>
                    <TableCell align="right">Werkboek €</TableCell>
                    <TableCell align="right">Verschil</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {maanden.map((m) => (
                    <TableRow key={m.periode} sx={{ bgcolor: m.afwijkend ? '#fff7ed' : 'transparent' }}>
                        <TableCell>{kortePeriode(m.periode)}</TableCell>
                        <TableCell align="right">{m.pct_forecast ? `${m.pct_forecast}%` : '–'}</TableCell>
                        <TableCell align="right">{m.eur_forecast ? formatEuro(m.eur_forecast) : '–'}</TableCell>
                        <TableCell align="right">{m.pct_excel ? `${m.pct_excel}%` : '–'}</TableCell>
                        <TableCell align="right">{m.eur_excel ? formatEuro(m.eur_excel) : '–'}</TableCell>
                        <TableCell align="right" sx={{ color: kleurDelta(m.delta_eur), fontWeight: m.afwijkend ? 600 : 400 }}>
                            {m.delta_eur ? `${m.delta_eur > 0 ? '+' : ''}${formatEuro(m.delta_eur)}` : '–'}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function ProjectRij({ p, formatEuro }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
                <TableCell sx={{ width: 32, p: 0.5 }}>
                    <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
                </TableCell>
                <TableCell><strong>{p.project_number}</strong></TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.project_name}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{p.company_name}{p.excel_status ? ` · ${p.excel_status}` : ''}</Typography>
                </TableCell>
                <TableCell>{p.pl || '—'}</TableCell>
                <TableCell align="right">{formatEuro(p.bbi_forecast)}</TableCell>
                <TableCell align="right">{formatEuro(p.bbi_excel)}</TableCell>
                <TableCell align="right" sx={{ color: kleurDelta(p.bbi_delta), fontWeight: Math.abs(p.bbi_delta) >= 250 ? 600 : 400 }}>
                    {p.bbi_delta ? `${p.bbi_delta > 0 ? '+' : ''}${formatEuro(p.bbi_delta)}` : '–'}
                </TableCell>
                <TableCell align="right" sx={{ color: kleurDelta(p.max_delta_eur) }}>{p.max_delta_eur ? formatEuro(p.max_delta_eur) : '–'}</TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {p.signalen.map((s) => (
                            <Tooltip key={s} title={SIGNAAL[s]?.uitleg || s}>
                                <Chip size="small" label={SIGNAAL[s]?.label || s} sx={{ bgcolor: `${SIGNAAL[s]?.kleur || '#64748b'}1a`, color: SIGNAAL[s]?.kleur || '#64748b', fontWeight: 600, fontSize: 11 }} />
                            </Tooltip>
                        ))}
                        {!p.signalen.length && <Chip size="small" label="Gelijk" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: 11 }} />}
                    </Box>
                </TableCell>
            </TableRow>
            {open && (
                <TableRow>
                    <TableCell colSpan={9} sx={{ bgcolor: '#f8fafc', py: 1 }}>
                        {p.excel_opmerking && <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#475569' }}>Opmerking werkboek: {p.excel_opmerking}</Typography>}
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#475569' }}>
                            Totaal gepland: tool {p.pct_forecast_totaal}% · werkboek {p.pct_excel_totaal}%
                        </Typography>
                        {p.maanden.length ? <MaandRegels maanden={p.maanden} formatEuro={formatEuro} /> : <Typography variant="caption">Geen maanden in deze periode.</Typography>}
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

/**
 * Planning in de tool (percentages per maand van omzet min inkoop) naast de maandverdeling van de BBI in
 * OnderhandenWerk_FF.xlsx. De engine haalt het werkboek zelf van SharePoint; er hoeft niets geüpload te worden.
 */
export default function PlanningVsOhw({ search, formatEuro }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('afwijkend');
    const [open, setOpen] = useState(true);

    const laad = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await authFetch(`${BACKEND_URL}/Api/forecasts/planning-vs-ohw`);
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.error || body.detail || `Ophalen mislukt (${res.status})`);
            setData(body);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const projecten = useMemo(() => {
        if (!data) return [];
        let lijst = data.projecten;
        if (filter === 'afwijkend') lijst = lijst.filter((p) => p.signalen.length);
        if (search) {
            const q = search.toLowerCase();
            lijst = lijst.filter((p) => `${p.project_number} ${p.project_name} ${p.company_name} ${p.pl}`.toLowerCase().includes(q));
        }
        return lijst;
    }, [data, filter, search]);

    const bestand = data?.bestand;
    const gewijzigd = bestand?.gewijzigd ? new Date(bestand.gewijzigd).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : null;
    const vandaag = new Date().toISOString().slice(0, 7);

    return (
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, bgcolor: '#fafafa', borderBottom: open ? '2px solid #0ea5e9' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Planning vs OHW</Typography>
                    {data && <Chip size="small" label={`${data.samenvatting.afwijkend} van ${data.samenvatting.in_beide} wijken af`} sx={{ bgcolor: data.samenvatting.afwijkend ? '#d97706' : '#16a34a', color: '#fff', fontWeight: 600 }} />}
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {data ? `${bestand?.naam || 'werkboek'}${gewijzigd ? ` · bijgewerkt ${gewijzigd}${bestand?.gewijzigd_door ? ` door ${bestand.gewijzigd_door}` : ''}` : ''}` : 'Maandverdeling in de tool naast het OHW-werkboek op SharePoint'}
                    </Typography>
                    <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                </Box>
                <Button size="small" variant={data ? 'outlined' : 'contained'} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <CloudSyncIcon />} onClick={laad} disabled={loading}>
                    {data ? 'Opnieuw vergelijken' : 'Vergelijk met SharePoint-bestand'}
                </Button>
            </Box>
            <Collapse in={open}>
                <Box sx={{ p: 2 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                    {!data && !loading && !error && (
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Vergelijkt per project de maandverdeling in de tool (percentages van omzet min inkoop) met de verdeling van de BBI in OnderhandenWerk_FF.xlsx.
                            Verschillen vanaf {formatEuro(250)} per maand of 10 procentpunt worden gemarkeerd. Het bestand wordt rechtstreeks van SharePoint gelezen.
                        </Typography>
                    )}
                    {data && (
                        <>
                            <TableContainer sx={{ mb: 2 }}>
                                <Table size="small" sx={{ '& td, & th': { py: 0.5, fontSize: 12, whiteSpace: 'nowrap' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Per maand</TableCell>
                                            {data.totalen.map((t) => (
                                                <TableCell key={t.periode} align="right" sx={{ fontWeight: 600, bgcolor: t.periode === vandaag ? '#e0f2fe' : 'transparent' }}>{kortePeriode(t.periode)}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Tool (projecten in beide)</TableCell>
                                            {data.totalen.map((t) => <TableCell key={t.periode} align="right">{formatEuro(t.forecast)}</TableCell>)}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Werkboek (zelfde projecten)</TableCell>
                                            {data.totalen.map((t) => <TableCell key={t.periode} align="right">{formatEuro(t.excel)}</TableCell>)}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Verschil</TableCell>
                                            {data.totalen.map((t) => (
                                                <TableCell key={t.periode} align="right" sx={{ fontWeight: 600, color: kleurDelta(t.delta, 1000) }}>{t.delta ? `${t.delta > 0 ? '+' : ''}${formatEuro(t.delta)}` : '–'}</TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ color: '#64748b' }}>OHW (BBI)-regel in het werkboek</TableCell>
                                            {data.totalen.map((t) => <TableCell key={t.periode} align="right" sx={{ color: '#64748b' }}>{t.ohw_blad != null ? formatEuro(t.ohw_blad) : '–'}</TableCell>)}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
                                    <ToggleButton value="afwijkend">Afwijkend ({data.samenvatting.afwijkend})</ToggleButton>
                                    <ToggleButton value="alle">Alle ({data.samenvatting.in_beide})</ToggleButton>
                                </ToggleButtonGroup>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    BBI afwijkend: {data.samenvatting.bbi_afwijkend} · planning afwijkend: {data.samenvatting.planning_afwijkend} ·
                                    open in werkboek zonder prognose: {data.samenvatting.excel_open_zonder_forecast} · prognose zonder regel in werkboek: {data.samenvatting.forecast_zonder_excel}
                                </Typography>
                            </Box>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell sx={{ fontWeight: 600 }}>Nr</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>PL</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>BBI tool</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>BBI werkboek</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Verschil BBI</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Grootste maandverschil</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Signaal</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {projecten.map((p) => <ProjectRij key={p.project_number} p={p} formatEuro={formatEuro} />)}
                                        {!projecten.length && (
                                            <TableRow><TableCell colSpan={9} sx={{ color: '#999', py: 2 }}>Geen projecten in deze selectie.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {(data.excel_open_zonder_forecast.length > 0 || data.forecast_zonder_excel.length > 0) && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Open in werkboek, geen prognose in de tool ({data.samenvatting.excel_open_zonder_forecast})</Typography>
                                        {data.excel_open_zonder_forecast.slice(0, 25).map((e) => (
                                            <Typography key={e.project_number} variant="caption" sx={{ display: 'block' }}>{e.project_name} · {e.pl || '—'} · BBI {formatEuro(e.bbi_excel)}</Typography>
                                        ))}
                                    </Paper>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Prognose in de tool, geen regel in werkboek ({data.samenvatting.forecast_zonder_excel})</Typography>
                                        {data.forecast_zonder_excel.slice(0, 25).map((f) => (
                                            <Typography key={f.project_number} variant="caption" sx={{ display: 'block' }}>{f.project_number} {f.project_name} · {f.company_name} · {formatEuro(f.project_value)}</Typography>
                                        ))}
                                    </Paper>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}
